import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fiscalService from './fiscalService';
import { toast } from 'react-toastify';

const initialState = {
  fiscalConfig: null,
  fiscalStatus: null,
  loading: false,
  error: false,
  success: false,
  message: ''
};

// Obtener configuración fiscal
export const getFiscalConfig = createAsyncThunk(
  'fiscal/getConfig',
  async (_, thunkAPI) => {
    try {
      return await fiscalService.getFiscalConfig();
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Guardar configuración fiscal
export const saveFiscalConfig = createAsyncThunk(
  'fiscal/saveConfig',
  async (data, thunkAPI) => {
    try {
      return await fiscalService.saveFiscalConfig(data);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Subir credenciales AFIP
export const uploadAfipCredentials = createAsyncThunk(
  'fiscal/uploadCredentials',
  async (formData, thunkAPI) => {
    try {
      return await fiscalService.uploadAfipCredentials(formData);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Agregar punto de venta
export const addPuntoVenta = createAsyncThunk(
  'fiscal/addPuntoVenta',
  async (data, thunkAPI) => {
    try {
      return await fiscalService.addPuntoVenta(data);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Actualizar punto de venta
export const updatePuntoVenta = createAsyncThunk(
  'fiscal/updatePuntoVenta',
  async (data, thunkAPI) => {
    try {
      return await fiscalService.updatePuntoVenta(data);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Obtener estado
export const getFiscalStatus = createAsyncThunk(
  'fiscal/getStatus',
  async (_, thunkAPI) => {
    try {
      return await fiscalService.getFiscalStatus();
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fiscalSlice = createSlice({
  name: 'fiscal',
  initialState,
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.error = false;
      state.success = false;
      state.message = '';
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Config
      .addCase(getFiscalConfig.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFiscalConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.fiscalConfig = action.payload;
      })
      .addCase(getFiscalConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.message = action.payload;
      })
      // Save Config
      .addCase(saveFiscalConfig.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveFiscalConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.fiscalConfig = action.payload.fiscalConfig;
        toast.success('Configuración guardada exitosamente');
      })
      .addCase(saveFiscalConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.message = action.payload;
        toast.error(action.payload);
      })
      // Upload Credentials
      .addCase(uploadAfipCredentials.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadAfipCredentials.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        toast.success('Credenciales AFIP cargadas exitosamente');
      })
      .addCase(uploadAfipCredentials.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.message = action.payload;
        toast.error(action.payload);
      })
      // Add Punto Venta
      .addCase(addPuntoVenta.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPuntoVenta.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (state.fiscalConfig) {
          state.fiscalConfig.puntosVenta = action.payload.puntosVenta;
        }
        toast.success('Punto de venta agregado');
      })
      .addCase(addPuntoVenta.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.message = action.payload;
        toast.error(action.payload);
      })
      // Update Punto Venta
      .addCase(updatePuntoVenta.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePuntoVenta.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (state.fiscalConfig) {
          state.fiscalConfig.puntosVenta = action.payload.puntosVenta;
        }
        toast.success('Punto de venta actualizado');
      })
      .addCase(updatePuntoVenta.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.message = action.payload;
        toast.error(action.payload);
      })
      // Get Status
      .addCase(getFiscalStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFiscalStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.fiscalStatus = action.payload;
      })
      .addCase(getFiscalStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.message = action.payload;
      });
  }
});

export const { reset } = fiscalSlice.actions;
export default fiscalSlice.reducer;
